import { differenceInSeconds, format, isValid, parseISO } from 'date-fns';

import { ActiveReconciliationRunStatuses } from 'app/constants/reconciliations';

import { ReconciliationRun } from 'app/types/reconciliations';

import { formatCurrency } from 'app/utils/strings';

/**
 * @description Whether a reconciliation run is still waiting or working, so its numbers can still change.
 */
export const isActiveReconciliationRun = (run?: Pick<ReconciliationRun, 'status'>): boolean =>
  !!run && ActiveReconciliationRunStatuses.includes(run.status);

/**
 * @description Turns a finding type such as COLUMN_TOTAL_MISMATCH into "Column total mismatch".
 */
export const formatFindingType = (type?: string | null): string => {
  if (!type) {
    return '-';
  }

  const words = type.toLowerCase().split('_');
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
};

/**
 * @description Formats a date and time from the backend, or a dash when there is none.
 */
export const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'dd MMM yyyy, HH:mm') : '-';
};

/**
 * @description How long a run took, or has taken so far, as "1h 2m 3s".
 */
export const formatRunDuration = (
  run: Pick<ReconciliationRun, 'startedAt' | 'completedAt'>,
  now: Date = new Date()
): string => {
  if (!run.startedAt) {
    return '-';
  }

  const start = parseISO(run.startedAt);
  const end = run.completedAt ? parseISO(run.completedAt) : now;
  if (!isValid(start) || !isValid(end)) {
    return '-';
  }

  const seconds = Math.max(differenceInSeconds(end, start), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts = [
    hours ? `${hours}h` : '',
    hours || minutes ? `${minutes}m` : '',
    `${seconds % 60}s`,
  ];

  return parts.filter(Boolean).join(' ');
};

/**
 * @description Describes which companies a run covered.
 */
export const formatRunScope = (companyIds: string[], companyNames: string[]): string => {
  if (companyIds.length === 0) {
    return 'All companies';
  }

  if (companyNames.length > 0 && companyNames.length <= 2) {
    return companyNames.join(', ');
  }

  return `${companyIds.length} ${companyIds.length === 1 ? 'company' : 'companies'}`;
};

/**
 * @description Reads an amount from the report, which may come as a number or as text.
 */
export const toAmount = (value?: number | string | null): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const amount = typeof value === 'number' ? value : Number(String(value).replace(/^'/, ''));
  return Number.isFinite(amount) ? amount : undefined;
};

/**
 * @description Formats an amount from the reconciliation as currency, or a dash when there is none.
 */
export const formatReconciliationAmount = (value?: number | string | null): string => {
  const amount = toAmount(value);
  return amount === undefined ? '-' : formatCurrency(amount);
};
